import { useCallback, useEffect, useState } from "react";

interface UseFormExitGuardParams {
  /** Há alterações não salvas? Liga o `beforeunload` e a confirmação ao sair. */
  isDirty: boolean;
  /**
   * Envio em andamento — desliga a guarda. A navegação que acontece logo após um
   * save é intencional, e o `beforeunload` não deve disparar durante o request.
   */
  isSubmitting?: boolean;
  /** Saída efetiva (navegar/fechar), chamada direto quando não há nada a perder ou após confirmar. */
  onExit: () => void;
}

/**
 * Guarda genérica de saída de formulário, reaproveitada por criação e edição.
 * Centraliza dois mecanismos que antes viviam espalhados (efeito `beforeunload`
 * nos componentes Form + `window.confirm` em cada `handleCancel`):
 *
 * - **`beforeunload`**: avisa o navegador ao fechar/recarregar a aba com alterações
 *   pendentes.
 * - **Confirmação ao sair pela app**: `requestExit` abre um diálogo estilizado
 *   (em vez do `window.confirm` cru) quando há alterações; sem alterações, sai direto.
 *
 * O componente renderiza o diálogo com os campos retornados (`isConfirmOpen`,
 * `confirmExit`, `cancelExit`) — ver `UnsavedChangesDialog`.
 */
export function useFormExitGuard({
  isDirty,
  isSubmitting = false,
  onExit,
}: UseFormExitGuardParams) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  // Avisa antes de fechar/recarregar a aba com alterações não salvas.
  useEffect(() => {
    if (!isDirty || isSubmitting) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty, isSubmitting]);

  // Tentativa de sair: com alterações pendentes pede confirmação; sem, sai direto.
  const requestExit = useCallback(() => {
    if (isDirty) setIsConfirmOpen(true);
    else onExit();
  }, [isDirty, onExit]);

  // Confirmou o descarte no diálogo.
  const confirmExit = useCallback(() => {
    setIsConfirmOpen(false);
    onExit();
  }, [onExit]);

  // Desistiu de sair — continua editando.
  const cancelExit = useCallback(() => setIsConfirmOpen(false), []);

  return {
    /** Dispare no "Cancelar"/"Voltar": abre a confirmação se houver alterações. */
    requestExit,
    /** Estado do diálogo de confirmação (alimenta `UnsavedChangesDialog`). */
    isConfirmOpen,
    setIsConfirmOpen,
    confirmExit,
    cancelExit,
  };
}

export type UseFormExitGuardReturn = ReturnType<typeof useFormExitGuard>;
